import { escapeHTML } from "./xss";

// Only these URL schemes may become clickable links. Blocks javascript:/data: injection.
const ALLOWED_SCHEMES = /^(https?:\/\/|mailto:)/i;

/**
 * Renders a small, safe subset of markdown to an HTML string.
 *
 * Supported syntax:
 *   - Links:      [text](url)   (only http/https/mailto schemes; otherwise rendered as plain text)
 *   - Bold:       **text**      -> <strong>
 *   - Italic:     _text_        -> <em>
 *   - Paragraph:  blank line    -> <p>
 *   - Line break: single \n     -> <br>
 *
 * Security model: the markdown source is authored by us (trusted), but it may contain
 * interpolated, producer-supplied values (name, email, url). We escape the whole string
 * first so any raw HTML in those values is neutralized before we generate our own tags.
 * Links are extracted into placeholders before bold/italic run, so an underscore inside
 * an email/url is never mistaken for an italic marker.
 *
 * @param {string} md - markdown source (already interpolated by i18next)
 * @returns {string} HTML string safe to inject via dangerouslySetInnerHTML
 */
function renderSimpleMarkdown(md) {
  if (!md || typeof md !== "string") return "";

  // 1. Escape raw HTML first (neutralizes injection via interpolated producer values).
  //    Markdown syntax chars ([ ] ( ) * _) are not touched by escapeHTML, so they survive.
  let safe = escapeHTML(md);

  // 2. Extract links into placeholders so bold/italic cannot corrupt their text/url.
  //    The %%LINKn%% token carries no markdown meaning and never occurs in escaped text,
  //    so it survives bold/italic without adding stray whitespace around the link.
  const links = [];
  safe = safe.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (match, text, url) => {
    if (!ALLOWED_SCHEMES.test(url)) return text; // disallowed scheme -> plain text
    const isExternal = /^https?:\/\//i.test(url);
    const attrs = isExternal ? ' target="_blank" rel="noopener noreferrer"' : "";
    links.push(`<a href="${url}"${attrs}>${text}</a>`);
    return `%%LINK${links.length - 1}%%`;
  });

  // 3. Inline emphasis on the link-free text.
  safe = safe.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  safe = safe.replace(/_([^_]+)_/g, "<em>$1</em>");

  // 4. Restore links.
  safe = safe.replace(/%%LINK(\d+)%%/g, (match, index) => links[Number(index)]);

  // 5. Block structure: blank lines split paragraphs, single newlines become <br>.
  return safe
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0)
    .map((block) => `<p>${block.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

export { renderSimpleMarkdown };
export default renderSimpleMarkdown;
